import { createSelector } from '@reduxjs/toolkit';

import { AssetContainerTypes, SourceEnum } from 'js/src/client';
import type { SearchContextValueType } from 'js/src/data/media';
import { checkAssetUUID, SEARCH_CONTEXT } from 'js/src/data/media';
import { UPLOAD_MODAL_MAX_VIDEO_DURATION_SECONDS } from 'js/src/data/media/media-types';
import type { PlainJsStoreOrmData } from 'js/src/data/plain-js-store-orm';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type {
    FileUpload,
    StoredUploadedAsset,
    UploadSession,
} from 'js/src/data/uploads/uploads-types';
import {
    FILE_STATUS,
    fileUploadAssetIsAssetContainer,
    MINIMUM_FILE_UPLOAD_DIMS,
} from 'js/src/data/uploads/uploads-types';
import { withStableEmptyArray } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

type StoredBackgroundOrAudioUpload = Extract<
    StoredUploadedAsset,
    { type: AssetContainerTypes.BACKGROUND | AssetContainerTypes.AUDIO }
>;
export type StoredUploadedAssetData = StoredBackgroundOrAudioUpload['asset'];

const EMPTY_FILES: PlainJsStoreOrmData<FileUpload, string> = PlainJsStoreORM.createEmpty<
    FileUpload,
    string
>().data;

type StoredBackgroundAsset = Extract<
    StoredUploadedAsset,
    { type: AssetContainerTypes.BACKGROUND }
>['asset'];

const getBackgroundAssetUuid = (asset: StoredBackgroundAsset) => asset[asset.type]?.uuid || null;

const getBackgroundAssetTitle = (asset: StoredBackgroundAsset) => asset[asset.type]?.title || null;

const uploadedAssetHasUuid = (
    uploadedAsset: StoredUploadedAsset,
    uuid: string,
): uploadedAsset is StoredBackgroundOrAudioUpload => {
    if (uploadedAsset.type === AssetContainerTypes.BACKGROUND) {
        return uploadedAsset.asset[uploadedAsset.asset.type]?.uuid === uuid;
    }
    if (uploadedAsset.type === AssetContainerTypes.AUDIO) {
        return uploadedAsset.asset.uuid === uuid;
    }
    return false;
};

export function getUploadSession(
    state: RootState,
    uploadSessionId: string | null,
): UploadSession | null | undefined {
    if (!uploadSessionId) {
        return null;
    }
    return state.uploads.upload_sessions[uploadSessionId];
}

export function getUploadedAssetByUuid(
    state: RootState,
    uuid: string,
): StoredUploadedAssetData | null {
    const { allIds, byId } = state.uploads.uploaded_assets;
    const lastMatchingUpload = allIds
        .map((id) => byId[id])
        .filter((uploadedAsset) => uploadedAssetHasUuid(uploadedAsset, uuid))
        .at(-1);
    return lastMatchingUpload?.asset ?? null;
}

export const getAllSessionUploadedAssetUuids = createSelector(
    (state: RootState) => state.uploads.uploaded_assets,
    (uploadedAssets): readonly string[] => {
        const uploadedAssetsOrm = new PlainJsStoreORM(uploadedAssets);
        const uuids = uploadedAssetsOrm.map((uploadedAsset) => {
            if (uploadedAsset.type === AssetContainerTypes.BACKGROUND) {
                const uuid = getBackgroundAssetUuid(uploadedAsset.asset);
                if (!uuid) throw new Error('Asset uuid not found– should be impossible');
                return uuid;
            }
            return checkAssetUUID(uploadedAsset.asset.uuid);
        });
        return withStableEmptyArray(uuids);
    },
);

export const getUploadedFileNames = createSelector(
    (state: RootState) => state.uploads.uploaded_assets,
    (uploadedAssets): readonly (string | null)[] => {
        const uploadedAssetsOrm = new PlainJsStoreORM(uploadedAssets);
        const names = uploadedAssetsOrm.map((uploadedAsset) => {
            if (uploadedAsset.type === AssetContainerTypes.BACKGROUND) {
                return getBackgroundAssetTitle(uploadedAsset.asset);
            }
            return uploadedAsset.asset.title;
        });
        return withStableEmptyArray(names);
    },
);

const selectFiles = (state: RootState, uploadSessionId: string) => {
    const session = getUploadSession(state, uploadSessionId);
    if (session) {
        return session.files;
    }
    return EMPTY_FILES;
};

export const selectFilesToUpload = createSelector([selectFiles], (selectedFiles) => {
    const orm = new PlainJsStoreORM(selectedFiles);
    return withStableEmptyArray(orm.filter((file) => file.user_canceled === false));
});

export const selectAllFilesInUploadSession = createSelector([selectFiles], (selectedFiles) => {
    const orm = new PlainJsStoreORM(selectedFiles);
    return withStableEmptyArray(orm.map((file) => file));
});

export const selectAllFileIdsInUploadSession = createSelector(
    [selectFiles],
    (selectedFiles): readonly string[] => selectedFiles.allIds,
);

// File uploads are still in the uploading process until they are either in the VALIDATED or FAILED status
export function getAreFilesFinishedUploading(state: RootState, uploadSessionId: string) {
    const orm = new PlainJsStoreORM(selectFiles(state, uploadSessionId));
    return orm
        .map((file) => file.status)
        .every((status) => [FILE_STATUS.VALIDATED, FILE_STATUS.FAILED].indexOf(status) !== -1);
}

export function getFilesCanBeEdited(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    if (!session) throw new Error('Upload session not found');
    return session.files_can_be_edited;
}

export function selectUploadsFolderId(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    return session?.folderId;
}

export function selectUploadsAttribution(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    if (!session) throw new Error('Upload session not found');
    return session.attribution;
}

export function selectUploadsMediaTags(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    if (!session) throw new Error('Upload session not found');
    return session.media_tags;
}

export function selectUploadsLicense(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    if (!session) throw new Error('Upload session not found');
    return session.license;
}

export function selectUploadsFailMessages(state: RootState) {
    return state.uploads.fail_messages;
}

export function getActualMediaUploadProgress(state: RootState) {
    const progressValues = Object.values(state.uploads.progress);
    if (progressValues.length <= 0) {
        return -1;
    }
    const total = progressValues.reduce((sum, progress) => sum + progress, 0);
    return total / progressValues.length;
}

export function shouldShowDurationWarning(mediaItem: FileUpload) {
    const durationSeconds = mediaItem.file_metadata.duration;
    if (durationSeconds) {
        const startMillis = mediaItem.file_metadata.start_millis || 0;
        const endMillis = mediaItem.file_metadata.end_millis || durationSeconds * 1000;
        const trimmedDurationSeconds = (endMillis - startMillis) / 1000;
        if (trimmedDurationSeconds > UPLOAD_MODAL_MAX_VIDEO_DURATION_SECONDS) {
            return true;
        }
    }
    return false;
}

export const getFileObj = (file: FileUpload) => {
    return file.file_obj;
};

export function shouldShowRecommendedMediaSizeWarning(files: readonly FileUpload[]) {
    return files.some((file) => {
        const uploadedAsset = file.uploaded_asset;
        if (
            !uploadedAsset ||
            !fileUploadAssetIsAssetContainer(uploadedAsset) ||
            uploadedAsset.type !== AssetContainerTypes.BACKGROUND
        ) {
            return false;
        }
        const background = uploadedAsset.background;
        const backgroundAsset = background?.[background.type];
        if (!backgroundAsset) {
            return false;
        }
        return (
            backgroundAsset.width < MINIMUM_FILE_UPLOAD_DIMS.width ||
            backgroundAsset.height < MINIMUM_FILE_UPLOAD_DIMS.height
        );
    });
}

// Get currently uploading files that match a specific asset type
export const getUploadingFilesBySearchContext = createSelector(
    (state: RootState) => state.uploads.upload_sessions,
    (state: RootState, searchContext: SearchContextValueType) => searchContext,
    (uploadSessions, searchContext) => {
        const assetTypes: AssetContainerTypes[] = [];
        switch (searchContext) {
            case SEARCH_CONTEXT.UPLOADED_ELEMENTS:
                assetTypes.push(AssetContainerTypes.ICON, AssetContainerTypes.BACKGROUND);
                break;
            case SEARCH_CONTEXT.UPLOADED_AUDIO:
                assetTypes.push(AssetContainerTypes.AUDIO);
                break;
            case SEARCH_CONTEXT.UPLOADED_VOICEOVERS:
                assetTypes.push(AssetContainerTypes.AUDIO);
                break;
            case SEARCH_CONTEXT.UPLOADED_VISUALS:
                assetTypes.push(AssetContainerTypes.BACKGROUND);
                break;
            default:
                return []; // No specific asset type for this context
        }
        const matchingFiles: Array<{ id: string; fileUpload: FileUpload }> = [];
        Object.values(uploadSessions).forEach((session) => {
            const orm = new PlainJsStoreORM(session.files);
            orm.allIds.forEach((fileId) => {
                const file = orm.getByIdOrThrow(fileId);
                const fileAssetType = file.upload_opts.asset_type;
                const source = file.upload_opts.metadata?.source;
                const status = file.status;
                const userCanceled = file.user_canceled;

                // Include files that are actively uploading or processing
                // Once validated/completed, they should appear as regular assets
                if (
                    assetTypes.includes(fileAssetType) &&
                    !userCanceled &&
                    (status === FILE_STATUS.LOADING || status === FILE_STATUS.FINALIZING)
                ) {
                    // Voice uploads should only appear in voice tab, music uploads only in music tab
                    switch (searchContext) {
                        case SEARCH_CONTEXT.UPLOADED_VOICEOVERS: {
                            if (source === SourceEnum.SOURCE_USER_VOICEOVER) {
                                matchingFiles.push({ id: fileId, fileUpload: file });
                            }

                            break;
                        }
                        case SEARCH_CONTEXT.UPLOADED_AUDIO: {
                            if (source === SourceEnum.SOURCE_USER) {
                                matchingFiles.push({ id: fileId, fileUpload: file });
                            }

                            break;
                        }
                        case SEARCH_CONTEXT.UPLOADED_ELEMENTS: {
                            if (
                                source === SourceEnum.SOURCE_USER &&
                                fileAssetType === AssetContainerTypes.ICON
                            ) {
                                matchingFiles.push({ id: fileId, fileUpload: file });
                            }
                            if (source === SourceEnum.SOURCE_USER_STICKERS) {
                                matchingFiles.push({ id: fileId, fileUpload: file });
                            }

                            break;
                        }
                        default: {
                            matchingFiles.push({ id: fileId, fileUpload: file });
                        }
                    }
                }
            });
        });
        return matchingFiles;
    },
);

// Get the session ID for a specific file
export const getSessionIdForFile = (state: RootState, fileId: string): string | null => {
    const sessionEntry = Object.entries(state.uploads.upload_sessions).find(([, session]) =>
        new PlainJsStoreORM(session.files).contains(fileId),
    );
    return sessionEntry ? sessionEntry[0] : null;
};

// Get the upload progress for a specific file
// NOTE: returns the progress for the WHOLE session; see getSingleFileUploadProgress for one file's own
export const getFileUploadProgress = (state: RootState, fileId: string): number => {
    const sessionId = getSessionIdForFile(state, fileId);
    if (!sessionId) return -1;

    const progress = state.uploads.progress[sessionId];
    return progress !== undefined ? progress : -1;
};

// One file's own upload progress (0-100), falling back to the session average when the
// controller hasn't reported per-file progress. Returns -1 when there is none yet.
export const getSingleFileUploadProgress = (state: RootState, fileId: string): number => {
    const sessionId = getSessionIdForFile(state, fileId);
    if (!sessionId) return -1;

    const fileName = new PlainJsStoreORM(state.uploads.upload_sessions[sessionId].files).getById(
        fileId,
    )?.file_obj?.name;
    const fileProgress = fileName
        ? state.uploads.file_progress?.[sessionId]?.[fileName]
        : undefined;
    return fileProgress ?? getFileUploadProgress(state, fileId);
};
